import { useEffect, useState } from "react";
import { GlassCard, PageHeader, Skeleton } from "../components/shared";
import { Mail, Briefcase, Phone, MapPin, Calendar, CreditCard, UserCheck, Shield } from "lucide-react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";

export default function TeamDirectory() {
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedEmp, setSelectedEmp] = useState(null);

    useEffect(() => {
        api.get("/employees")
            .then((res) => {
                setEmployees(res.data.items || []);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const getAvatarBg = (name) => {
        const colors = [
            "bg-teal-600", "bg-blue-600", "bg-pink-600", "bg-emerald-600", 
            "bg-violet-600", "bg-amber-600", "bg-rose-600", "bg-cyan-600"
        ];
        let hash = 0;
        const n = String(name || "");
        for (let i = 0; i < n.length; i++) {
            hash = n.charCodeAt(i) + ((hash << 5) - hash);
        }
        return colors[Math.abs(hash) % colors.length];
    };

    const getInitials = (name) => {
        if (!name) return "EE";
        const parts = String(name).trim().split(/\s+/);
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return String(name).substring(0, 2).toUpperCase();
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "—";
        try {
            const date = new Date(dateStr);
            const day = date.getDate();
            const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
            const month = months[date.getMonth()];
            const year = date.getFullYear();
            return `${day} ${month} ${year}`;
        } catch (e) {
            return dateStr;
        }
    };

    const renderRoleBadge = (role) => {
        const r = String(role || "employee").toLowerCase();
        if (r === "lead") {
            return (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-emerald-50 text-emerald-600 dark:bg-emerald-950/20 dark:text-emerald-400 border border-emerald-200/50 dark:border-emerald-800/30">lead</span>
            );
        } else if (r === "founder" || r === "super_admin") {
            return (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-teal-50 text-teal-600 dark:bg-teal-950/20 dark:text-teal-400 border border-teal-200/50 dark:border-teal-800/30">founder</span>
            );
        } else {
            return (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400">{r}</span>
            );
        }
    };

    return (
        <div>
            <PageHeader title="Team" subtitle="Browse and search team members across departments." />
            
            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[1, 2, 3].map((n) => (
                        <GlassCard key={n} className="p-6 h-48 space-y-4">
                            <Skeleton className="w-12 h-12 rounded-full" />
                            <Skeleton className="h-4 w-1/2" />
                            <Skeleton className="h-3 w-3/4" />
                        </GlassCard>
                    ))}
                </div>
            ) : employees.length === 0 ? (
                <GlassCard className="p-6 text-center text-muted-foreground">
                    No active team members found.
                </GlassCard>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {employees.map((emp) => (
                        <GlassCard key={emp.id} className="p-6 relative flex flex-col justify-between border border-teal-100/50 dark:border-teal-900/50 hover:shadow-md transition duration-300">
                            <div>
                                <div className="flex items-start gap-4 mb-4">
                                    {emp.avatar ? (
                                        <img src={emp.avatar} alt={emp.name} className="w-12 h-12 rounded-full object-cover border" />
                                    ) : (
                                        <div className={`w-12 h-12 rounded-full ${getAvatarBg(emp.name)} text-white flex items-center justify-center font-bold text-base uppercase shadow-sm shrink-0`}>
                                            {getInitials(emp.name)}
                                        </div>
                                    )}
                                    <div className="min-w-0">
                                        <h3 onClick={() => setSelectedEmp(emp)} className="font-heading font-bold text-slate-800 dark:text-slate-100 cursor-pointer hover:text-teal-600 transition truncate">{emp.name}</h3>
                                        <span className="text-[11px] font-semibold text-muted-foreground block truncate">{emp.designation || "Team Lead / Engineer"}</span>
                                        
                                        {/* Row of Badges */}
                                        <div className="flex flex-wrap gap-1.5 mt-2">
                                            {renderRoleBadge(emp.role)}
                                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600 border border-slate-200/50 dark:bg-slate-800 dark:text-slate-400">
                                                {emp.employee_id || "PHXXXX"}
                                            </span>
                                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600 border border-slate-200/50 dark:bg-slate-800 dark:text-slate-400">
                                                {emp.department || "General"}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="space-y-2 mt-4 pt-3 border-t border-black/5 dark:border-white/5 text-[11px] font-medium text-slate-600 dark:text-slate-300">
                                    <div className="flex items-center gap-2">
                                        <Mail className="w-3.5 h-3.5 text-teal-600" />
                                        <a href={`mailto:${emp.email}`} className="hover:underline hover:text-teal-600 truncate">{emp.email}</a>
                                    </div>
                                    {emp.phone && (
                                        <div className="flex items-center gap-2">
                                            <Phone className="w-3.5 h-3.5 text-teal-600" />
                                            <a href={`tel:${emp.phone}`} className="hover:underline hover:text-teal-600">{emp.phone}</a>
                                        </div>
                                    )}
                                    {emp.location && (
                                        <div className="flex items-center gap-2">
                                            <MapPin className="w-3.5 h-3.5 text-teal-600" />
                                            <span>{emp.location}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            <div className="mt-4 pt-3 border-t border-black/5 dark:border-white/5 flex justify-between items-center text-[10px]">
                                <span className="text-muted-foreground font-semibold italic">
                                    {emp.reporting_manager ? `Reports to ${emp.reporting_manager}` : "Reports to Founder Office"}
                                </span>
                                <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    onClick={() => setSelectedEmp(emp)}
                                    className="h-6 text-[10px] px-2 py-0 text-teal-600 font-bold uppercase hover:bg-teal-50 dark:hover:bg-teal-950/20"
                                >
                                    View Full Profile
                                </Button>
                            </div>
                        </GlassCard>
                    ))}
                </div>
            )}

            {/* Profile Detail Dialog */}
            <Dialog open={!!selectedEmp} onOpenChange={(open) => !open && setSelectedEmp(null)}>
                <DialogContent className="sm:max-w-[450px]">
                    <DialogHeader>
                        <DialogTitle>Employee Profile</DialogTitle>
                    </DialogHeader>
                    {selectedEmp && (
                        <div className="mt-4 space-y-5">
                            <div className="flex items-center gap-4 border-b pb-4">
                                {selectedEmp.avatar ? (
                                    <img src={selectedEmp.avatar} alt={selectedEmp.name} className="w-16 h-16 rounded-full object-cover border shadow-sm" />
                                ) : (
                                    <div className={`w-16 h-16 rounded-full ${getAvatarBg(selectedEmp.name)} text-white flex items-center justify-center font-bold text-xl uppercase shadow-sm`}>
                                        {getInitials(selectedEmp.name)}
                                    </div>
                                )}
                                <div>
                                    <h3 className="font-heading font-bold text-lg text-slate-800 dark:text-slate-100">{selectedEmp.name}</h3>
                                    <p className="text-sm text-muted-foreground">{selectedEmp.designation || "Team Member"}</p>
                                    <span className="inline-block mt-1 text-[10px] font-bold bg-teal-50 text-teal-600 border border-teal-200/50 rounded-full px-2 py-0.5 uppercase dark:bg-teal-950/30 dark:text-teal-400">
                                        {selectedEmp.role?.replace("_", " ")}
                                    </span>
                                </div>
                            </div>

                            <div className="space-y-3.5 text-sm font-medium">
                                <div className="flex items-center gap-3">
                                    <CreditCard className="w-4 h-4 text-teal-600" />
                                    <div>
                                        <div className="text-xs text-muted-foreground">Employee ID</div>
                                        <div>{selectedEmp.employee_id || "PHXXXX"}</div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Briefcase className="w-4 h-4 text-teal-600" />
                                    <div>
                                        <div className="text-xs text-muted-foreground">Department</div>
                                        <div>{selectedEmp.department || "General"}</div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Mail className="w-4 h-4 text-teal-600" />
                                    <div>
                                        <div className="text-xs text-muted-foreground">Email</div>
                                        <div>{selectedEmp.email}</div>
                                    </div>
                                </div>
                                {selectedEmp.phone && (
                                    <div className="flex items-center gap-3">
                                        <Phone className="w-4 h-4 text-teal-600" />
                                        <div>
                                            <div className="text-xs text-muted-foreground">Phone</div>
                                            <div>{selectedEmp.phone}</div>
                                        </div>
                                    </div>
                                )}
                                {selectedEmp.location && (
                                    <div className="flex items-center gap-3">
                                        <MapPin className="w-4 h-4 text-teal-600" />
                                        <div>
                                            <div className="text-xs text-muted-foreground">Location</div>
                                            <div>{selectedEmp.location}</div>
                                        </div>
                                    </div>
                                )}
                                <div className="flex items-center gap-3">
                                    <Calendar className="w-4 h-4 text-teal-600" />
                                    <div>
                                        <div className="text-xs text-muted-foreground">Joining Date</div>
                                        <div>{formatDate(selectedEmp.joining_date)}</div>
                                    </div>
                                </div>
                                {selectedEmp.reporting_manager && (
                                    <div className="flex items-center gap-3">
                                        <UserCheck className="w-4 h-4 text-teal-600" />
                                        <div>
                                            <div className="text-xs text-muted-foreground">Reporting Lead</div>
                                            <div>{selectedEmp.reporting_manager}</div>
                                        </div>
                                    </div>
                                )}
                                <div className="flex items-center gap-3">
                                    <Shield className="w-4 h-4 text-teal-600" />
                                    <div>
                                        <div className="text-xs text-muted-foreground">Status</div>
                                        <div className="capitalize">{selectedEmp.status || "Active"}</div>
                                    </div>
                                </div>
                            </div>

                            <Button onClick={() => setSelectedEmp(null)} className="w-full rounded-xl bg-teal-600 hover:bg-teal-700 text-white font-semibold">
                                Close
                            </Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
